import * as THREE from 'three';
import type { Obstacle } from '../../../shared/game/world-types.js';
import type { SceneryBatches } from './scenery-batches.js';
import type { TreeWind } from './wind-material.js';

const strata = [0x5c5a57, 0x716559, 0x676057, 0x86705a, 0x6c6460, 0x7a6a56];
const moss = [0x6c7345, 0x7a7a4a, 0x5f6c48];
const hash = (x: number, y: number, z: number) => { const v = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453; return v - Math.floor(v); };

/** One faceted, banded tier. The footprint is the authoritative obstacle; jitter never leaves it. */
function tierGeometry(radiusX: number, radiusZ: number, bottom: number, top: number, bottomScale: number, topScale: number, seed: number, topColor: THREE.Color) {
  const geometry = new THREE.CylinderGeometry(topScale, bottomScale, top - bottom, 9, 3);
  const position = geometry.getAttribute('position'), normal = geometry.getAttribute('normal'), colors: number[] = [];
  const color = new THREE.Color();
  for (let i = 0; i < position.count; i++) {
    const unitY = position.getY(i) / (top - bottom) + .5;
    // Seam vertices share positions, so a position hash keeps the mesh watertight.
    const jitter = hash(Math.round(position.getX(i) * 50), Math.round(unitY * 3), Math.round(position.getZ(i) * 50) + seed);
    const edge = Math.abs(normal.getY(i)) < .5;
    position.setXYZ(i, position.getX(i) * radiusX * (edge ? .9 + jitter * .12 : 1), position.getY(i) + (bottom + top) / 2 + (edge ? (jitter - .5) * (top - bottom) * .1 : 0), position.getZ(i) * radiusZ * (edge ? .9 + jitter * .12 : 1));
    if (normal.getY(i) > .5) color.copy(topColor).multiplyScalar(.9 + jitter * .2);
    else color.setHex(strata[(Math.floor(unitY * 3 + bottom * .12 + seed) + strata.length * 4) % strata.length]).multiplyScalar(.82 + jitter * .3 + unitY * .1);
    colors.push(color.r, color.g, color.b);
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3)); geometry.computeVertexNormals(); return geometry;
}

const material = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1 });

/** A stepped, striated rock mass with a mossy crown, boulders at its foot and stunted pines on top. */
export function buildCliff(scene: THREE.Scene, obstacle: Obstacle, random: () => number, batches: SceneryBatches, rock: THREE.Material, trees: TreeWind,
  geometries: { rock: THREE.BufferGeometry; crown: THREE.BufferGeometry; trunk: THREE.BufferGeometry }) {
  const { x, z, width, depth } = obstacle, height = obstacle.height ?? 20, group = new THREE.Group();
  const tiers = [{ h: .5, s: 1 }, { h: .3, s: .78 }, { h: .2, s: .55 }];
  let y = -.1, scale = 1;
  const crown = new THREE.Color(moss[Math.floor(random() * moss.length)]);
  for (const [index, tier] of tiers.entries()) {
    const next = tiers[index + 1]?.s ?? tier.s * .72, offset = index ? (random() - .5) * (1 - tier.s) * .3 : 0;
    const geometry = tierGeometry(width * .46, depth * .46, y, y + tier.h * height, scale, next, Math.floor(random() * 90), crown);
    const mesh = new THREE.Mesh(geometry, material); mesh.position.set(offset * width, 0, offset * depth); mesh.castShadow = mesh.receiveShadow = true; group.add(mesh);
    y += tier.h * height; scale = next;
  }
  const base = batches.height(x, z);
  group.position.set(x, base, z); group.rotation.y = 0; scene.add(group);
  // Rubble apron and ledge boulders sit inside the footprint so visible rock matches collision.
  for (let i = 0; i < 12; i++) {
    const angle = random() * Math.PI * 2, r = .3 + random() * .12, s = Math.min(width, depth) * (.05 + random() * .06);
    batches.add('ridge-rock', geometries.rock, rock, x + Math.cos(angle) * width * r, s * .4, z + Math.sin(angle) * depth * r, s, s * .7, s, random() * 6, [0x686b65, 0x807a69, 0x786f62, 0x8a7a62][i % 4]);
  }
  // A few wind-bent pines cling to the crown.
  const topScale = .4;
  for (let i = 0; i < Math.min(7, Math.ceil(width * depth / 900)); i++) {
    const angle = random() * Math.PI * 2, r = random() * topScale * .55, h = 3 + random() * 3.2;
    const px = x + Math.cos(angle) * width * .46 * r, pz = z + Math.sin(angle) * depth * .46 * r;
    const lift = base - batches.height(px, pz);
    batches.add('ridge-trunk', geometries.trunk, trees.sway(rock, -.5), px, height + h * .3 + lift, pz, .22, h * .6, .22, 0, 0x594b40);
    batches.add('ridge-pine', geometries.crown, trees.material(-.7 / .75), px, height + h * .7 + lift, pz, 1.2 + random(), h * .75, 1.2 + random(), 0, [0x44574a, 0x566650][i % 2]);
  }
}
